// components/validationHelpers.js
export const EPS = 0.01; // 1 ban tolerance

export function cents(n) { return Math.round(Number(n) * 100); }
export function fromc(c) { return Math.round(c) / 100; }
export function near(a, b, eps = EPS) { return Math.abs(a - b) <= eps; }
export function isInt(n) { return Number.isInteger(n); }

export function explainBackendError(e) {
  try {
    if (typeof e === "string") {
      try {
        const j = JSON.parse(e);
        return j.detail || j.message || j.error || e;
      } catch {
        return e;
      }
    }
    if (e && typeof e === "object") {
      return e.detail || e.message || e.error || JSON.stringify(e).slice(0, 2000);
    }
  } catch {}
  return "A apărut o eroare. Te rugăm să încerci din nou.";
}

/**
 * Each combo line must have selections for every category and priced products where required.
 * @param {Object} line - combo cart line (from state)
 * @param {Object} priceByProductId - pricelist map { [productId]: priceRON }
 * @param {Object} [promoByProductId] - optional promo map { [productId]: promoPriceRON }
 */
export function validateComboSelections(line, priceByProductId, promoByProductId) {
  const missing = [];
  const badPrice = [];
  const badQty = [];

  if (!line.selectionDetails || !Array.isArray(line.selectionDetails)) {
    return { ok: false, reason: "Combo invalid (fără selecții)." };
  }
  if (!isInt(line.qty) || line.qty <= 0) badQty.push(line.name || line.id);

  for (const s of line.selectionDetails) {
    if (!s || !s.categoryId || !s.productId) {
      missing.push(s?.categoryId || "?");
      continue;
    }
    // Consider item priced if present in pricelist OR in promo map OR priceType is 0/2 (included/fixed)
    const hasListPrice = priceByProductId[s.productId] != null;
    const hasPromo     = promoByProductId && promoByProductId[s.productId] != null;
    const pricedByType = (s.priceType === 0) || (s.priceType === 2);
    const hasPrice = hasListPrice || hasPromo || pricedByType;

    if (!hasPrice) badPrice.push(s.name || s.productId);
  }

  if (missing.length) return { ok: false, reason: `Lipsesc opțiuni pentru categorii: ${missing.join(", ")}` };
  if (badPrice.length) return { ok: false, reason: `Produs fără preț în listă: ${badPrice.join(", ")}` };
  if (badQty.length) return { ok: false, reason: `Cantitate invalidă pentru combo: ${badQty.join(", ")}` };
  return { ok: true };
}

/**
 * Order-level validation just before POST.
 * NOTE: priceByProductId is used only for normal price lines; promo lines don't require pricelist lookup.
 * @param {Object} payload
 * @param {Object} priceByProductId - pricelist map { [productId]: priceRON }
 */
export function validateOrderPayload(payload, priceByProductId) {
  const errs = [];

  if (!payload || typeof payload !== "object") {
    errs.push("Payload invalid.");
    return { ok: false, errors: errs };
  }

  const prods         = Array.isArray(payload.orderProductEntry_order) ? payload.orderProductEntry_order : [];
  const prodsPromo    = Array.isArray(payload.orderProductPromotionEntry_order) ? payload.orderProductPromotionEntry_order : [];
  const combos        = Array.isArray(payload.orderComboEntry_order) ? payload.orderComboEntry_order : [];
  const combosPromo   = Array.isArray(payload.orderComboPromotionEntry_order) ? payload.orderComboPromotionEntry_order : [];

  // Empty cart guard must count ALL four arrays
  if ((prods.length + prodsPromo.length + combos.length + combosPromo.length) === 0) {
    errs.push("Coșul este gol.");
  }

  // ---- Normal product lines (ProductPrice) ----
  for (const p of prods) {
    if (!isInt(p.quantity) || p.quantity <= 0) errs.push(`Cantitate invalidă la produs.`);
    if (!p.ProductPrice?.product) errs.push(`Lipsește iref produs.`);
    // ONLY check pricelist for normal price lines
    if (p.ProductPrice?.product && priceByProductId[p.ProductPrice.product] == null) {
      errs.push(`Produs fără preț în listă: ${p.ProductPrice.product}`);
    }
    if (typeof p.value !== "number") errs.push(`Valoare invalidă la produs.`);
    if (typeof p.vat !== "number") errs.push(`TVA invalid la produs.`);
  }

  // ---- Promo product lines (ProductPromotion) ----
  for (const p of prodsPromo) {
    if (!isInt(p.quantity) || p.quantity <= 0) errs.push(`Cantitate invalidă la produs promo.`);
    if (!p.ProductPromotion?.product) errs.push(`Lipsește iref produs (promo).`);
    // Do NOT require pricelist price for promo products
    if (typeof p.value !== "number") errs.push(`Valoare invalidă la produs promo.`);
    if (typeof p.vat !== "number") errs.push(`TVA invalid la produs promo.`);
  }

  // ---- Normal combo lines (ComboPrice) ----
  for (const c of combos) {
    if (!isInt(c.quantity) || c.quantity <= 0) errs.push(`Cantitate invalidă la combo.`);
    if (!c.ComboPrice?.productCombo) errs.push(`Lipsește iref combo.`);
    if (typeof c.value !== "number") errs.push(`Valoare invalidă la combo.`);
    if (typeof c.vat !== "number") errs.push(`TVA invalid la combo.`);

    const comps = c.OrderComboProductsEntry_OrderComboEntry;
    if (!Array.isArray(comps) || comps.length === 0) {
      errs.push(`Combo fără componente.`);
    } else {
      const sumAll = comps.reduce((a, b) => a + Number(b.value || 0), 0);
      if (!near(sumAll, c.value, 0.02)) {
        errs.push(`Suma componentelor nu corespunde valorii combo (${sumAll.toFixed(2)} vs ${Number(c.value).toFixed(2)}).`);
      }
    }
  }

  // ---- Promo combo lines (ComboPromotion) ----
  for (const c of combosPromo) {
    if (!isInt(c.quantity) || c.quantity <= 0) errs.push(`Cantitate invalidă la combo promo.`);
    if (!c.ComboPromotion?.productCombo) errs.push(`Lipsește iref combo (promo).`);
    if (typeof c.value !== "number") errs.push(`Valoare invalidă la combo promo.`);
    if (typeof c.vat !== "number") errs.push(`TVA invalid la combo promo.`);

    const comps = c.OrderComboPromotionProductsEntry_OrderComboPromotionEntry;
    if (!Array.isArray(comps) || comps.length === 0) {
      errs.push(`Combo promo fără componente.`);
    } else {
      const sumAll = comps.reduce((a, b) => a + Number(b.value || 0), 0);
      if (!near(sumAll, c.value, 0.02)) {
        errs.push(`Suma componentelor (promo) nu corespunde valorii combo (${sumAll.toFixed(2)} vs ${Number(c.value).toFixed(2)}).`);
      }
    }
  }

  const gross = Number(payload.totalAmount || 0);
  const net   = Number(payload.totalAmountWithoutVAT || 0);
  if (!(gross >= 0 && net >= 0)) errs.push("Totaluri invalide.");
  if (net > gross + EPS) errs.push("Total fără TVA depășește totalul cu TVA.");

  return { ok: errs.length === 0, errors: errs };
}